import React, { Fragment, useState, useEffect } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon, PencilIcon } from "@heroicons/react/24/outline";
import { Button } from "components/ui";
import { toast } from "sonner";
import { renameDocument } from "services/documentFolderService";
import { ConfirmModal } from "components/shared/ConfirmModal";

/**
 * Modal to rename an existing document/file
 */
export function RenameDocumentModal({ isOpen, onClose, onRenameSuccess, document: doc }) {
  const [baseName, setBaseName] = useState("");
  const [extension, setExtension] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmRename, setShowConfirmRename] = useState(false);

  useEffect(() => {
    if (isOpen && doc) {
      const full = doc.name || doc.fileName || doc.FileName || "";
      const lastDotIndex = full.lastIndexOf(".");

      if (lastDotIndex > 0) {
        setBaseName(full.substring(0, lastDotIndex));
        setExtension(full.substring(lastDotIndex));
      } else {
        setBaseName(full);
        setExtension("");
      }
      setShowConfirmRename(false);
    }
  }, [isOpen, doc]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!baseName.trim()) {
      toast.error("Please enter a valid document name");
      return;
    }

    const newFullName = `${baseName.trim()}${extension}`;
    const currentFullName = doc?.name || doc?.fileName || doc?.FileName || "";

    if (newFullName === currentFullName) {
      onClose();
      return;
    }

    setShowConfirmRename(true);
  };

  const handleConfirmRename = async () => {
    const newFullName = `${baseName.trim()}${extension}`;
    const docId = doc?.id || doc?.DocumentId || doc?.documentId;

    setIsSubmitting(true);
    try {
      const res = await renameDocument({
        documentId: docId,
        fileName: newFullName,
      });

      if (res.success) {
        toast.success(res.message || "Document renamed successfully");
        setShowConfirmRename(false);
        onClose();
        if (onRenameSuccess) onRenameSuccess(docId, newFullName);
      } else {
        toast.error(res.error || "Failed to rename document");
      }
    } catch (err) {
      toast.error(err.message || "Error renaming document");
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentFullName = doc?.name || doc?.fileName || doc?.FileName || "";
  const newFullName = `${baseName.trim()}${extension}`;

  return (
    <>
      <Transition appear show={Boolean(isOpen)} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-[300] flex flex-col items-center justify-center p-4 sm:px-5"
          onClose={onClose}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="absolute inset-0 bg-gray-900/50 backdrop-blur dark:bg-black/30" />
          </TransitionChild>

          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="dark:bg-dark-700 relative flex w-full max-w-md origin-top flex-col overflow-hidden rounded-lg bg-white shadow-xl transition-all">
              <div className="dark:bg-dark-800 flex items-center justify-between bg-gray-100 px-4 py-3 sm:px-5 border-b border-gray-200 dark:border-dark-600">
                <DialogTitle
                  as="h3"
                  className="dark:text-dark-100 flex items-center gap-2 text-base font-semibold text-gray-900"
                >
                  <PencilIcon className="h-5 w-5 text-primary-500" />
                  Rename Document
                </DialogTitle>
                <Button
                  onClick={onClose}
                  variant="flat"
                  isIcon
                  className="size-7 rounded-full"
                  disabled={isSubmitting}
                >
                  <XMarkIcon className="size-4.5" />
                </Button>
              </div>

              <form onSubmit={handleSubmit} className="p-4 sm:p-5 space-y-4">
                <div>
                  <label
                    htmlFor="rename-doc-name"
                    className="block text-xs font-medium text-gray-700 dark:text-dark-200 mb-1"
                  >
                    Document Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative flex items-center rounded-lg border border-gray-300 bg-white dark:border-dark-600 dark:bg-dark-800 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/20 overflow-hidden">
                    <input
                      id="rename-doc-name"
                      type="text"
                      value={baseName}
                      onChange={(e) => setBaseName(e.target.value)}
                      autoFocus
                      className="w-full bg-transparent px-3 py-2 text-sm text-gray-900 dark:text-dark-50 placeholder-gray-400 dark:placeholder-dark-400 outline-none"
                    />
                    {extension && (
                      <span className="px-3 py-2 text-sm font-medium text-gray-500 bg-gray-100 dark:bg-dark-700 dark:text-dark-300 border-l border-gray-200 dark:border-dark-600 select-none shrink-0">
                        {extension}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <Button
                    type="button"
                    onClick={onClose}
                    variant="outlined"
                    className="min-w-[5.5rem] rounded-full"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    className="min-w-[5.5rem] rounded-full"
                    disabled={isSubmitting || !baseName.trim()}
                  >
                    Save
                  </Button>
                </div>
              </form>
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>

      {/* Confirmation modal for document rename */}
      <ConfirmModal
        show={showConfirmRename}
        onClose={() => setShowConfirmRename(false)}
        onOk={handleConfirmRename}
        confirmLoading={isSubmitting}
        state="pending"
        buttonColor="primary"
        messages={{
          pending: {
            title: "Rename Document",
            description: `Are you sure you want to rename "${currentFullName}" to "${newFullName}"?`,
            actionText: "Rename",
          },
          success: {
            title: "Document Renamed",
            description: "The document has been successfully renamed.",
            actionText: "Done",
          },
          error: {
            title: "Rename Failed",
            description: "Failed to rename the document. Please try again.",
            actionText: "Retry",
          },
        }}
      />
    </>
  );
}

export default RenameDocumentModal;
